import type { AgentSubscription } from "schemas/agent-billing";
import type { WorkspacePaymentProcessor } from "schemas/generated/billing";

import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { Tag } from "@replohq/lattice/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
  useIsAgentBillingEnabled,
} from "@v2/contexts/WorkspaceDashboardContext";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";
import { subscriptionRouter } from "@v2/trpc/routes/subscription";
import { workspaceRouter } from "@v2/trpc/routes/workspace";

import {
  ALWAYS_FRESH,
  CopyableId,
  formatDate,
  KeyValue,
  StripeLink,
} from "./shared";

const PAYMENT_PROCESSOR_LABELS = {
  none: "None",
  shopify: "Shopify",
  stripe: "Stripe",
} satisfies Record<WorkspacePaymentProcessor | "none", string>;

const SubscriptionDetails: React.FC<{
  activeSubscription: AgentSubscription | null | undefined;
  isLoading: boolean;
}> = ({ activeSubscription, isLoading }) => {
  if (isLoading) {
    return (
      <div className="text-muted py-3 text-xs">Loading subscription...</div>
    );
  }

  if (!activeSubscription) {
    return (
      <div className="text-muted py-3 text-xs">
        No agent billing subscription found.
      </div>
    );
  }

  return (
    <>
      <KeyValue label="Tier">
        {activeSubscription.plan ? (
          <Tag variant="neutral" size="sm">
            {activeSubscription.plan}
          </Tag>
        ) : (
          "--"
        )}
      </KeyValue>
      <KeyValue label="Interval">
        <Tag variant="neutral" size="sm">
          {activeSubscription.interval}
        </Tag>
      </KeyValue>
      <KeyValue label="Started At">
        {formatDate(activeSubscription.startedAt)}
      </KeyValue>
      <KeyValue label="Cancel At">
        {formatDate(activeSubscription.cancelAt)}
      </KeyValue>
      <KeyValue label="Period End">
        {formatDate(activeSubscription.currentPeriodEnd)}
      </KeyValue>
      <KeyValue label="Stripe Subscription ID">
        <StripeLink value={activeSubscription.stripeSubscriptionId} />
      </KeyValue>
      <KeyValue label="Stripe Customer ID">
        <StripeLink value={activeSubscription.stripeCustomerId} />
      </KeyValue>
    </>
  );
};

export const SubscriptionSummary: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const isAgentBilling = useIsAgentBillingEnabled();
  const currentPaymentProcessor =
    workspace?.paymentProcessor === "stripe" ||
    workspace?.paymentProcessor === "shopify"
      ? workspace.paymentProcessor
      : "none";
  const { mutate: updatePaymentProcessor, isPending: isSwitchingProcessor } =
    workspaceRouter.updatePaymentProcessor.useMutation({
      onSuccess: () => {
        window.location.reload();
      },
    });
  const {
    data: activeAgentSubscription,
    isLoading: isAgentSubscriptionLoading,
  } = agentBillingRouter.subscription.find.useQuery(
    workspaceId ? { workspaceId } : skipToken,
    ALWAYS_FRESH,
  );
  const {
    data: activeLegacySubscription,
    isLoading: isLegacySubscriptionLoading,
  } = subscriptionRouter.getActiveSubscription.useQuery(
    workspaceId ? { workspaceId } : skipToken,
    ALWAYS_FRESH,
  );
  const hasActiveSubscription = Boolean(
    activeAgentSubscription || activeLegacySubscription,
  );
  const isSubscriptionLoading =
    isAgentSubscriptionLoading || isLegacySubscriptionLoading;
  const preferredBillingAction = (() => {
    if (currentPaymentProcessor === "stripe") {
      return (
        <Tag variant="neutral" size="sm">
          Locked
        </Tag>
      );
    }
    if (hasActiveSubscription) {
      return (
        <Tag variant="neutral" size="sm">
          Subscription active
        </Tag>
      );
    }
    return (
      <Button
        variant="secondary"
        size="sm"
        loading={isSubscriptionLoading || isSwitchingProcessor}
        disabled={!workspaceId || isSubscriptionLoading || isSwitchingProcessor}
        onClick={() => {
          if (!workspaceId) {
            return;
          }
          updatePaymentProcessor({
            workspaceId,
            paymentProcessor: "stripe",
          });
        }}
      >
        Switch to Stripe
      </Button>
    );
  })();

  return (
    <div className="flex flex-col divide-y divide-border">
      <KeyValue label="Workspace ID">
        <CopyableId value={workspaceId} />
      </KeyValue>
      <KeyValue label="Preferred billing">
        <span className="flex items-center gap-2">
          <Tag variant="neutral" size="sm">
            {PAYMENT_PROCESSOR_LABELS[currentPaymentProcessor]}
          </Tag>
          {preferredBillingAction}
        </span>
      </KeyValue>
      <KeyValue label="Agent Billing">
        <Tag variant={isAgentBilling ? "success" : "neutral"} size="sm">
          {isAgentBilling ? "enabled" : "disabled"}
        </Tag>
      </KeyValue>
      <SubscriptionDetails
        activeSubscription={activeAgentSubscription}
        isLoading={isAgentSubscriptionLoading}
      />
    </div>
  );
};
